// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/recurring-donation.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / actions / custom-tabs).

import { createElement } from "react";
import type { DetailPageConfig } from "../../components/composition/DetailPage";
import { StageChip } from "../../components/composition/StageChip";
import { recurringDonationsApi } from "../../api/recurring-donations";
import type { RecurringDonation } from "../../api/types";
// override-block:imports BEGIN
import { Link } from "react-router-dom";
import type { RecurringDonationStatus, RecurringFrequency } from "../../api/types";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// CHG-1325 — Tag taxonomy phase 3: Tags tab via EntityTagsSection.
import { EntityTagsSection } from "../../components/composition/EntityTagsSection";
// RDM-R08 — real sub-resource tab renderers + gated ACI surface + the
// imperative lifecycle action emitter (pause/resume/cancel/reconcile).
import {
  RecurringChargesTab,
  RecurringCommunicationsTab,
  RecurringAciSurface,
} from "../../components/recurring/RecurringDetailTabs";
import { runRecurringDonationAction } from "../../lib/recurring-donation-actions";
// override-block:imports END

// override-block:helpers BEGIN
// RDM-R08 — labels mirror the runtime enums (7 statuses, 4 frequencies). The
// pre-RDM-R08 maps omitted pending/suspended/needs_reconciliation and carried
// the invalid biweekly/annual values; rows in the missing states fell through
// to the raw enum string.
const STATUS_LABEL: Record<RecurringDonationStatus, string> = {
  pending: "Pending",
  active: "Active",
  paused: "Paused",
  past_due: "Past due",
  canceled: "Canceled",
  suspended: "Suspended",
  needs_reconciliation: "Needs reconciliation",
};
const FREQUENCY_LABEL: Record<RecurringFrequency, string> = {
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually",
};

// RDM-R08 — the charge-retry max is ORG-CONFIGURABLE (default 3, NOT a
// hardcoded 5) and is NOT surfaced on the RecurringRead response, so the
// Overview tab no longer invents a max. The previous `MAX_RETRIES = 5` +
// its retry_policy.py citation (that module path does not exist) were
// removed; retry pressure now shows the raw attempt count.

function donorLabel(r: RecurringDonation): string {
  if (r.donor_name) return r.donor_name;
  if (r.donor_email) return r.donor_email;
  if (r.donor_phone) return r.donor_phone;
  return r.donor_id ? r.donor_id.slice(0, 8) + "…" : "Unknown donor";
}
// override-block:helpers END

export const recurringDonationDetailConfig: DetailPageConfig<RecurringDonation> = {
  idParam: "recurringId",
  queryKey: (id: string) => ["recurring-donations", "detail", id] as const,
  queryFn: (id: string) => recurringDonationsApi.getRecurringDonation(id),
  backTo: "/recurring",
  // override-block:title BEGIN
  // entity-hint title_field is amount_minor_units — but a bare cents number is a
  // terrible page title. Synthesize a friendly title at render time
  // ("Recurring · monthly") and put donor label in the subtitle.
  title: (r) => {
    const freq = FREQUENCY_LABEL[r.frequency] ?? r.frequency;
    return `Recurring · ${freq.toLowerCase()}`;
  },
  // override-block:title END
  // override-block:subtitle BEGIN
  subtitle: (r) => donorLabel(r),
  // override-block:subtitle END
  stageChip: (r) =>
    createElement(StageChip, {
      stage_id: r.stage,
      entity: "recurring",
      size: "lg",
    }),
  statusCards: (r, _fmt) => [
    {
      id: "amount",
      label: "Amount",
      value: r.amount_minor_units,
      format: "currency",
      currency: r.currency,
      tone: "g-giving",
    },
    // override-block:statcard-next-charge BEGIN
    {
      id: "next_charge",
      label: "Next charge",
      value: r.next_charge_at ?? null,
      // StatCardFormat doesn't include "relative-date" — use text + render
      // override to keep the formatter contract intact (same pattern as
      // asset-detail.ts last_used handling).
      format: "text",
      tone: "g-home",
      render: r.next_charge_at
        ? createElement("span", { className: "tabular" }, _fmt.formatDate(r.next_charge_at, "relative"))
        : createElement("span", { style: { color: "var(--color-text-muted)" } }, "—"),
    },
    // override-block:statcard-next-charge END
    {
      id: "retries",
      label: "Retries",
      value: r.retry_count,
      format: "number",
      tone: "g-events",
    },
    // override-block:statcard-health-score BEGIN
    {
      id: "health_score",
      label: "Health score",
      value: r.health_score ?? null,
      format: r.health_score != null ? "number" : "text",
      tone: "g-mygiving",
      render: r.health_score != null
        ? createElement("span", { className: "tabular" }, `${r.health_score.toFixed(0)} / 100`)
        : createElement("span", { style: { color: "var(--color-text-muted)" } }, "—"),
    },
    // override-block:statcard-health-score END
  ],
  actions: (r) => [
    // override-block:actions BEGIN
    // RDM-R08 — wired to REAL mutations via the imperative emitter (the config
    // callback is hookless; the <RecurringDonationActionsHost/> at the App root
    // owns the react-query mutation + invalidation). Previously these were
    // label-only buttons with no onClick — they couldn't mutate anything.
    // Verb visibility follows status: a canceled donation can't be paused, a
    // needs_reconciliation donation surfaces Reconcile (the un-park action).
    ...(r.status === "needs_reconciliation"
      ? [
          {
            id: "reconcile",
            label: "Reconcile",
            variant: "primary" as const,
            permissionGate: toGate(GATE_CATALOG.recurring_detail["reconcile"], "disable"),
            onClick: () => runRecurringDonationAction("reconcile", r),
          },
        ]
      : []),
    ...(r.status === "active" || r.status === "past_due"
      ? [
          {
            id: "pause",
            label: "Pause",
            variant: "primary" as const,
            permissionGate: toGate(GATE_CATALOG.recurring_detail["pause"], "disable"),
            onClick: () => runRecurringDonationAction("pause", r),
          },
        ]
      : []),
    ...(r.status === "paused" || r.status === "suspended"
      ? [
          {
            id: "resume",
            label: "Resume",
            variant: "primary" as const,
            permissionGate: toGate(GATE_CATALOG.recurring_detail["resume"], "disable"),
            onClick: () => runRecurringDonationAction("resume", r),
          },
        ]
      : []),
    ...(r.status !== "canceled"
      ? [
          {
            id: "cancel",
            label: "Cancel",
            variant: "ghost" as const,
            permissionGate: toGate(GATE_CATALOG.recurring_detail["cancel"], "disable"),
            onClick: () => runRecurringDonationAction("cancel", r),
          },
        ]
      : []),
    // override-block:actions END
  ],
  tabs: [
    // override-block:custom-tabs BEGIN
    // RDM-R08 — tabs are now contract-accurate:
    //   Overview        — key facts + raw retry count (no invented max) +
    //                     a donor link + the GATED ACI intent surface.
    //   Charges         — REAL data via GET /recurring-donations/{id}/charges.
    //   Communications  — REAL data via GET /recurring-donations/{id}/delivery-logs
    //                     (renamed from "Notes"; there is no notes endpoint).
    //   Tags            — EntityTagsSection (CHG-1325).
    // REMOVED: the "Schedule" tab (cited /timeline?kind=schedule — the route
    // ignores `kind` and projects no forward schedule; no /schedule endpoint
    // exists) and the "Donor" tab (cited /recurring-donations/{id}/donor —
    // that route never existed). The donor is reachable via the Overview link.
    {
      id: "overview",
      label: "Overview",
      render: (r, fmt) => {
        const freq = FREQUENCY_LABEL[r.frequency] ?? r.frequency;
        const amountStr = fmt.formatCurrency(r.amount_minor_units, r.currency);
        return createElement(
          "div",
          { style: { display: "flex", flexDirection: "column", gap: 16 } },
          // Key facts card
          createElement(
            "div",
            { className: "card", style: { padding: 16, fontSize: 13, lineHeight: 1.6 } },
            createElement("div", null, "Charging ", amountStr, " ", freq.toLowerCase()),
            createElement(
              "div",
              null,
              "Status: ",
              STATUS_LABEL[r.status] ?? r.status,
            ),
            // RDM-R08 — raw retry attempt count. The retry max is
            // org-configurable (default 3) and is NOT on the read response,
            // so we don't fabricate a "/ N" ceiling.
            r.retry_count > 0
              ? createElement(
                  "div",
                  null,
                  "Failed-charge retries: ",
                  fmt.formatNumber(r.retry_count),
                )
              : null,
            createElement(
              "div",
              null,
              "Started: ",
              fmt.formatDate(r.created_at, "long"),
            ),
            createElement(
              "div",
              null,
              "Last charged: ",
              r.last_charged_at ? fmt.formatDate(r.last_charged_at, "relative") : "Never",
            ),
            createElement(
              "div",
              null,
              "Next charge: ",
              r.next_charge_at ? fmt.formatDate(r.next_charge_at, "relative") : "—",
            ),
            // RDM-R08 — donor link (v1 parity: navigate to the donor detail
            // page). Only when donor_id is present (public phone-only rows may
            // still carry a donor_id; anonymous rows do not).
            r.donor_id
              ? createElement(
                  "div",
                  { style: { marginTop: 8 } },
                  createElement(
                    Link,
                    { to: `/donors/${r.donor_id}`, className: "link" },
                    "View donor — ",
                    donorLabel(r),
                  ),
                )
              : null,
            r.provider_reference
              ? createElement(
                  "div",
                  { style: { marginTop: 8, color: "var(--color-text-muted)" } },
                  "Provider ref: ",
                  r.provider_reference,
                )
              : null,
          ),
          // RDM-R08 — ACI intent-provenance surface, GATED on intent_key +
          // aci.kb.read (v1 parity). Renders nothing when either gate fails.
          createElement(RecurringAciSurface, {
            recurringId: r.id,
            intentKey: r.intent_key,
          }),
        );
      },
    },
    {
      id: "charges",
      label: "Charges",
      render: (r) => createElement(RecurringChargesTab, { recurringId: r.id }),
    },
    {
      id: "communications",
      label: "Communications",
      render: (r) => createElement(RecurringCommunicationsTab, { recurringId: r.id }),
    },
    // CHG-1325 — Tag taxonomy phase 3. Tags applied to this recurring donation.
    {
      id: "tags",
      label: "Tags",
      render: (r) =>
        createElement(EntityTagsSection, {
          entityType: "recurring_donation",
          entityId: r.id,
        }),
    },
    // override-block:custom-tabs END
  ],
};
